import {
  BRAND_SELECT,
  BRANDS_FETCH,
  BRANDS_FETCH_FAILURE,
  BRANDS_FETCH_SUCCESS,
  FETCH_RESET,
  GAME_RESET,
  GAME_RUN,
  GAME_RUN_FAILURE,
  GAME_RUN_SUCCESS,
  GAMES_FETCH,
  GAMES_FETCH_FAILURE,
  GAMES_FETCH_SUCCESS,
  PROVIDER_SELECT,
  PROVIDERS_FETCH,
  PROVIDERS_FETCH_FAILURE,
  PROVIDERS_FETCH_SUCCESS,
  SEARCH_QUERY_SET,
  TAG_FAVORITE_SELECT,
  TAG_SELECT,
  TAGS_FETCH,
  TAGS_FETCH_FAILURE,
  TAGS_FETCH_SUCCESS,
  UPDATE_FAVORITE_GAME,
} from "@shared/constants";
import {
  jackpotsVisibilityMap,
  SHOW_ALL,
  TAG_DEFAULT_ID,
  TAG_FAVORITE,
  TAG_MOBILE,
  TECHNICAL_TAGS,
} from "@shared/constants/game";
import { TAG_RECOMMENDED } from "@shared/modules/recommended-games";
import { initialErrorState, setError } from "@shared/reducers/error";
import {
  AddIsHiddenToGame,
  getItemById,
  incObjectValue,
  isWrongID,
  sortTags,
} from "@shared/reducers/utils";
import { filterService } from "@shared/services/query-filter-service/query-filter-service";
import { isEmpty } from "lodash";

let isComputedProviders = false;
let countOfGamesBy = { isCounted: false, providers: {}, brands: {}, tags: {} };

/** @typedef Fetch
 *  @type {("initial", "pending", "fulfilled", "rejected")} */
const INITIAL_FETCH_STATES = {
  providersFetch: "initial",
  brandsFetch: "initial",
  gamesFetch: "initial",
  gameRun: "initial",
};

/**
 * @param {Object} provider
 * @param {Object} obj - Additional attributes
 * @return {Object}
 * @description Add `games_count` & `game` to the provider.
 */
const getComputedProvider = (provider, obj = {}) => {
  const data = countOfGamesBy.providers[provider.id] || {};
  const games_count = data.games_count || 0;
  const result = Object.assign(provider, obj);

  result.games_count = games_count;
  if (games_count === 1) {
    result.game = data.game || "";
  }

  return result;
};

const initialState = {
  games: null,
  gameSession: null,
  computedGames: null,

  providers: null,
  currentProvider: {},
  currentProviderId: null,
  computedProviders: null,

  isJackpotsShown: false,

  brands: null,
  computedBrands: null,
  currentBrandId: SHOW_ALL,

  tags: null,
  computedTags: null,
  currentTagId: SHOW_ALL,
  countOfFavoriteGames: 0,
  isFavoriteFilter: false,
  searchQuery: "",
  error: initialErrorState,
  ...INITIAL_FETCH_STATES,
};

function gamesReducer(state = initialState, action) {
  switch (action.type) {
    case GAMES_FETCH:
      state = {
        ...state,
        gamesFetch: "pending",
      };
      break;

    case GAMES_FETCH_SUCCESS:
      {
        const games = action.payload.filter((game) => {
          return game.tags.every(
            (tag) => tag.name.toLowerCase() !== TAG_MOBILE
          );
        });

        const [providers, brands, tags] = [{}, {}, {}];

        /** Games analysis... */
        for (const game of games) {
          const { game_section_id: brand_id, game_provider_id: provider_id } =
            game;

          incObjectValue(brands, brand_id, provider_id);
          incObjectValue(providers, provider_id, "games_count");

          if (!providers.firstProvider) {
            providers.firstProvider = provider_id;
          }

          if (game.tags) {
            game.tags.forEach((tag) => {
              incObjectValue(tags, tag.id);

              switch (tag.name) {
                case "jackpot":
                  game.is_jackpot = true;
                  break;

                case "new":
                  game.is_new = true;
                  break;

                default:
              }
            });
          }

          if (!providers[provider_id].game) {
            providers[provider_id].game = game.string_id;
          }

          game.lowerCaseName = game.name.toLowerCase();
        }

        countOfGamesBy = {
          isCounted: true,
          providers,
          brands,
          tags,
        };

        state = {
          ...state,
          games,
          gamesFetch: "fulfilled",
        };
      }
      break;

    case GAMES_FETCH_FAILURE:
      state = {
        ...state,
        gamesFetch: "rejected",
      };
      break;

    case PROVIDERS_FETCH:
      state = {
        ...state,
        providersFetch: "pending",
      };
      break;

    case PROVIDERS_FETCH_SUCCESS:
      {
        let currentProvider = {};
        let currentProviderId = state.currentProviderId
          ? state.currentProviderId
          : null;

        if (countOfGamesBy.isCounted) {
          isComputedProviders = true;
        }

        const providers = action.payload.map((provider) => {
          getComputedProvider(provider, {
            lowerCaseName: provider.name.toLowerCase(),
          });
          if (currentProviderId === null && provider.games_count > 1) {
            currentProviderId = parseInt(provider.id);
          }
          if (currentProviderId === provider.id) {
            currentProvider = provider;
          }
          return provider;
        });

        const isJackpotsShown =
          jackpotsVisibilityMap[currentProvider.lowerCaseName];

        state = {
          ...state,
          providers,
          currentProvider,
          isJackpotsShown,
          currentProviderId,
          providersFetch: "fulfilled",
        };
      }
      break;

    case PROVIDERS_FETCH_FAILURE:
      state = {
        ...state,
        providers: null,
        providersFetch: "rejected",
        currentProviderId: null,
      };
      break;

    case PROVIDER_SELECT:
      {
        const currentProvider = getItemById(state.providers, action.payload);
        const isJackpotsShown =
          jackpotsVisibilityMap[currentProvider?.lowerCaseName];

        if (
          isEmpty(currentProvider) &&
          !filterService.getFilterParams?.provider?.id
        ) {
          filterService.setParam("provider", state.providers?.[0]);
        } else {
          if (action.payload !== filterService.getFilterParams?.provider?.id) {
            filterService.setParam("brandId", -1);
            filterService.setParam("provider", currentProvider);
          }
        }

        state = {
          ...state,
          currentProvider,
          isJackpotsShown,
          currentProviderId: action.payload,
          currentBrandId: SHOW_ALL,
          currentTagId: SHOW_ALL,
        };
      }
      break;

    case BRANDS_FETCH:
      state = {
        ...state,
        brandsFetch: "pending",
      };
      break;

    case BRANDS_FETCH_SUCCESS:
      state = {
        ...state,
        brands: action.payload,
        brandsFetch: "fulfilled",
      };
      break;

    case BRANDS_FETCH_FAILURE:
      state = {
        ...state,
        brands: null,
        brandsFetch: "rejected",
        currentBrandId: SHOW_ALL,
      };
      break;

    case BRAND_SELECT:
      filterService.setParam("brandId", action.payload);

      state = {
        ...state,
        currentBrandId: action.payload,
        currentTagId: SHOW_ALL,
      };
      break;

    case TAGS_FETCH:
      state = {
        ...state,
        tagsFetch: "pending",
      };
      break;

    case TAGS_FETCH_SUCCESS:
      state = {
        ...state,
        tags: sortTags(action.payload),
        tagsFetch: "fulfilled",
      };
      break;

    case TAGS_FETCH_FAILURE:
      state = {
        ...state,
        tags: null,
        tagsFetch: "rejected",
        currentTagId: SHOW_ALL,
      };
      break;

    case TAG_SELECT:
      state = {
        ...state,
        currentTagId: action.payload,
      };

      filterService.setParam("tagId", action.payload);

      if (action.payload === TAG_RECOMMENDED) {
        return { ...state };
      }

      break;
    case TAG_FAVORITE_SELECT:
      state = {
        ...state,
        isFavoriteFilter: action.payload,
      };
      break;

    case UPDATE_FAVORITE_GAME: {
      const { gameId, isFavorite } = action.payload;
      for (const game of state.games) {
        if (game.id === gameId) {
          game.is_favorite = isFavorite;
        }
      }
      break;
    }

    case SEARCH_QUERY_SET:
      state = {
        ...state,
        searchQuery: action.payload,
      };
      break;

    case GAME_RUN:
      state = {
        ...state,
        gameRun: "pending",
      };
      break;

    case GAME_RUN_SUCCESS:
      state = {
        ...state,
        gameSession: action.payload,
        gameRun: "fulfilled",
      };
      break;

    case GAME_RUN_FAILURE:
      state = {
        ...state,
        gameSession: null,
        error: setError(state.error, action.payload, 2),
        gameRun: "rejected",
      };
      break;

    case GAME_RESET:
      state = {
        ...state,
        gameSession: null,
        ...INITIAL_FETCH_STATES,
      };
      break;

    case FETCH_RESET:
      state = {
        ...state,
        ...INITIAL_FETCH_STATES,
      };
      break;

    default:
      break;
  }

  if (
    [
      TAG_SELECT,
      TAG_FAVORITE_SELECT,
      BRAND_SELECT,
      PROVIDER_SELECT,
      PROVIDERS_FETCH_SUCCESS,
      BRANDS_FETCH_SUCCESS,
      GAMES_FETCH_SUCCESS,
      TAGS_FETCH_SUCCESS,
      SEARCH_QUERY_SET,
      UPDATE_FAVORITE_GAME,
    ].includes(action.type) &&
    Array.isArray(state.tags) &&
    Array.isArray(state.games) &&
    Array.isArray(state.brands) &&
    Array.isArray(state.providers)
  ) {
    const { currentProviderId, currentBrandId, searchQuery, isFavoriteFilter } =
      state;
    let countOfFavoriteGames = 0;
    let [computedGames, computedTags] = [[], []];
    const computedCountOfGamesByTagID = {};
    // Get ID of the current tag
    let currentTagId =
      action.type === UPDATE_FAVORITE_GAME ||
      action.type === TAG_SELECT ||
      action.type === SEARCH_QUERY_SET ||
      action.type === TAG_FAVORITE_SELECT
        ? state.currentTagId
        : countOfGamesBy.tags[TAG_DEFAULT_ID]
        ? TAG_DEFAULT_ID
        : SHOW_ALL;

    /** Games analysis. Get computedGames... */
    for (const game of state.games) {
      let isWrongProviderID = isWrongID(
        currentProviderId,
        game?.game_provider_id
      );

      if (game.is_favorite) {
        countOfFavoriteGames++;
      }

      let isHidden =
        isWrongProviderID ||
        isWrongID(currentBrandId, game.game_section_id) ||
        !game.lowerCaseName.includes(searchQuery);

      if (isFavoriteFilter) {
        isWrongProviderID = false;
        isHidden =
          !game.lowerCaseName.includes(searchQuery) ||
          (isFavoriteFilter && !game.is_favorite);
      }

      if (!isWrongProviderID) {
        if (!isHidden && game.tags) {
          game.tags.forEach((tag) => {
            incObjectValue(computedCountOfGamesByTagID, tag.id);
          });
        }

        computedGames.push(AddIsHiddenToGame(game, isHidden));
      }
    }

    if (currentTagId !== SHOW_ALL && currentTagId !== TAG_FAVORITE) {
      if (computedCountOfGamesByTagID[currentTagId]) {
        computedGames = computedGames.map((game) => {
          const tagPredicate = game.tags.every(
            (tag) => tag.id !== currentTagId
          );

          return game.is_hidden ? game : AddIsHiddenToGame(game, tagPredicate);
        });
      } else {
        currentTagId = SHOW_ALL;
      }
    }

    const computedProviders = isComputedProviders
      ? state.providers
      : state.providers.map((provider) => getComputedProvider(provider));

    const computedBrands = state.brands.map((brand) => {
      const countOfGamesByProvider = countOfGamesBy.brands[brand.id] || {};

      return {
        ...brand,
        games_count: countOfGamesByProvider[currentProviderId] || 0,
      };
    });

    for (const tag of state.tags) {
      if (!TECHNICAL_TAGS.has(tag.name) && countOfGamesBy.tags[tag.id]) {
        computedTags.push({
          ...tag,
          games_count: computedCountOfGamesByTagID[tag.id] || 0,
        });
      }
    }

    state = {
      ...state,
      computedProviders,
      computedBrands,
      computedGames,
      computedTags,
      currentTagId,
      countOfFavoriteGames,
    };
  }
  return state;
}

export default gamesReducer;
